import { inject } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, concatMap, map, catchError, of } from 'rxjs';

import { ZenitEventSuccess, ZenitEventError, ZenitClickEvent } from '../../actions/zenit.actions';

import { getCmsConfigValue } from '@core/infrastructure/state/core.selectors';
import { MediasPublicModels, MediasPublicConstants } from '@features/products/medias';
import { WishlistPublicActions, WishlistPublicSelectors } from '@features/wishlist';
import { AddItemsWishlistAction } from '@features/wishlist/infrastructure/state/wishlist-commands.actions';
import { getProductPageDetail } from '@pages/product';
import { SetColorOnSelectedProduct } from '@pages/product/store/product-page.actions';
import { AnalyticsClickSourceID, EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';
import { ClickProductoBuscadorAnalytics, ObservedElementClick, ProductClickAnalytics, RelatedProductClickAnalytics, SeleccionarTallaAnalytics } from '@shared/analytics/store/app-events-analytics.actions';
import { ClickEvent, ZenitClickAction } from '@shared/analytics/ZENIT/models/events/click.model';
import { ZenitEventName } from '@shared/analytics/ZENIT/models/zenit-general.model';
import { ZenitEventApiService } from '@shared/analytics/ZENIT/services/api/zenit-event-api.service';
import { ZenitEventParserService } from '@shared/analytics/ZENIT/services/parser/zenit-event-parser.service';
import { ProductViewConfig } from '@shared/product/models';
import { AdjustItemQuantityShopcart, DeleteItemsShopcart } from '@shared/shopcart/store/shopcart.actions';

/** add-qty */
/** remove-qty */
/**
 * Gestiona los casos en los que se añade o se elimina una cantidad de un producto en el carrito.
 */
const clickAddQuantityShopcart$ = createEffect((
    actions$ = inject(Actions),
    zenitEventParserService = inject(ZenitEventParserService),
) => actions$.pipe(
    ofType(AdjustItemQuantityShopcart),
    filter(({ item, quantity }) => quantity > item?.quantity || quantity < item?.quantity),
    map(({ item, quantity }) => {
        const action = quantity > item?.quantity ? ZenitClickAction['add-qty'] : ZenitClickAction['remove-qty'];
        return zenitEventParserService.getEventObject<ClickEvent>(
            ZenitEventName.Click,
            EventFrom.shopcart,
            {
                items: [item],
                action,
                quantity: Math.abs(quantity - item?.quantity),
            },
        );
    }),
    map((clickEvent) => ZenitClickEvent({ clickEvent })),
), { functional: true });

const getEventFromOrigin: Partial<Record<MediasPublicModels.PublicMediaLocation, EventFrom>> = {
    [MediasPublicConstants.PublicMediaLocationType.category_page]: EventFrom.parrilla,
    [MediasPublicConstants.PublicMediaLocationType.related_product]: EventFrom.relateds,
    [MediasPublicConstants.PublicMediaLocationType.search]: EventFrom.searchResults,
    [MediasPublicConstants.PublicMediaLocationType.ZOOM_2]: EventFrom.parrilla,
    [MediasPublicConstants.PublicMediaLocationType.ZOOM_3]: EventFrom.parrilla,
};

const ADD_TO_CART_CLICK_IDS = [
    AnalyticsClickSourceID.PDPAddToCartMainCTA,
    AnalyticsClickSourceID.productViewShowQuickly,
    AnalyticsClickSourceID.wishlistItemAddToCart,
];

/** add-to-cart (QUICK BUY, PDP) */
/**
 * Gestiona los casos en los que se añade un producto al carrito desde el PDP, el quick buy o los items de wishlist.
 */
const clickAddToCart$ = createEffect((
    actions$ = inject(Actions),
    zenitEventParserService = inject(ZenitEventParserService),
) => actions$.pipe(
    ofType(ObservedElementClick),
    filter(({ observeType, observedData }) => observeType === 'analyticsClick' && ADD_TO_CART_CLICK_IDS.includes(observedData?.clickId)),
    map(({ observedData }) => {
        const productViewConfig: ProductViewConfig = observedData?.productViewConfig;
        const eventFrom = observedData?.eventFrom || productViewConfig?.eventFrom
            || getEventFromOrigin[productViewConfig?.location];
        const item = { ...observedData.product, idMedia: productViewConfig?.media?.idMedia }; // Se incluye idMedia para identificar el objeto de media que corresponde

        return zenitEventParserService.getEventObject<ClickEvent>(
            ZenitEventName.Click,
            eventFrom,
            {
                action: ZenitClickAction['add-to-cart'],
                items: [item],
                place: productViewConfig?.place,
                quantity: 1,
                relatedsSectionId: productViewConfig?.relatedsSectionId,
            },
        );
    }),
    map((clickEvent) => ZenitClickEvent({ clickEvent })),
), { functional: true });

/** add-to-cart (BUNDLE) */
/**
 * Gestiona los casos en los que se añade un producto desde el selector de talla de los items de la PDP de bundle.
 */
const clickAddToCartFichaBundle$ = createEffect((
    actions$ = inject(Actions),
    zenitEventParserService = inject(ZenitEventParserService),
) => actions$.pipe(
    ofType(ObservedElementClick),
    filter(({ observeType, observedData }) => observeType === 'analyticsClick' && observedData?.clickId === AnalyticsClickSourceID.productAddToCartSelectorSelect),
    map(({ observedData }) => {
        return zenitEventParserService.getEventObject<ClickEvent>(
            ZenitEventName.Click,
            EventFrom.fichaBundle,
            {
                action: ZenitClickAction['add-to-cart'],
                items: [observedData?.product],
            },
        );
    }),
    map((clickEvent) => ZenitClickEvent({ clickEvent })),
), { functional: true });

/** select-item (PRODUCT VIEW) */
/**
 * Gestiona los clicks en product-view de parrillas y listas de relacionados.
 */
const clickSelectItemActions$ = createEffect((
    actions$ = inject(Actions),
    zenitEventParserService = inject(ZenitEventParserService),
) => actions$.pipe(
    ofType(
        ClickProductoBuscadorAnalytics, // No se utiliza
        ProductClickAnalytics,
        RelatedProductClickAnalytics,
    ),
    map((action) => {
        return zenitEventParserService.getEventObject<ClickEvent>(
            ZenitEventName.Click,
            (action as any)?.eventFrom || (action as any)?.parentType,
            {
                action: ZenitClickAction['select-item'],
                items: [action?.product],
                place: (action as any)?.place,
                relatedsSectionId: (action as any)?.relatedsSectionId,
            },
        );
    }),
    map((clickEvent) => ZenitClickEvent({ clickEvent })),
), { functional: true });

const SELECT_ITEM_CLICK_IDS = [
    AnalyticsClickSourceID.shopcartItemImage,
    AnalyticsClickSourceID.shopcartItemName,
];

/** select-item (UBICACIONES QUE NO SON PRODUCT-VIEW) */
/**
 * Gestiona los clicks en el nombre e imagen de los items del carrito.
 */
const clickSelectItemElementObserver$ = createEffect((
    actions$ = inject(Actions),
    zenitEventParserService = inject(ZenitEventParserService),
) => actions$.pipe(
    ofType(ObservedElementClick),
    filter(({ observeType, observedData }) => observeType === 'analyticsClick' && SELECT_ITEM_CLICK_IDS.includes(observedData?.clickId)),
    map(({ observedData }) => {
        return zenitEventParserService.getEventObject<ClickEvent>(
            ZenitEventName.Click,
            observedData?.eventFrom,
            {
                action: ZenitClickAction['select-item'],
                clickSourceID: observedData?.clickId,
                items: [observedData?.product],
            },
        );
    }),
    map((clickEvent) => ZenitClickEvent({ clickEvent })),
), { functional: true });

/** select-size */
/**
 * Gestiona los casos en los que se selecciona una talla en la funcionalidad de productos coordinados, wishlist, PDP y compra rápida.
 */
const clickSelectSize$ = createEffect((
    actions$ = inject(Actions),
    zenitEventParserService = inject(ZenitEventParserService),
) => actions$.pipe(
    ofType(SeleccionarTallaAnalytics),
    map(({ product, config }) => {
        const eventFromParsed = config?.eventFrom || getEventFromOrigin[config?.location];

        return zenitEventParserService.getEventObject<ClickEvent>(
            ZenitEventName.Click,
            eventFromParsed,
            {
                action: ZenitClickAction['select-size'],
                items: [product],
                place: config?.place,
                relatedsSectionId: config?.relatedsSectionId,
                quantity: 1,
            },
        );
    }),
    map((clickEvent) => ZenitClickEvent({ clickEvent })),
), { functional: true });

/** select-color */
/**
 * Gestiona la selección de color en PDP.
 */
const clickSelectColor$ = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    zenitEventParserService = inject(ZenitEventParserService),
) => actions$.pipe(
    ofType(SetColorOnSelectedProduct),
    map(() => {
        const product = store.selectSignal(getProductPageDetail)();
        return zenitEventParserService.getEventObject<ClickEvent>(
            ZenitEventName.Click,
            EventFrom.productdetail,
            {
                action: ZenitClickAction['select-color'],
                items: [product],
            },
        );
    }),
    map((clickEvent) => ZenitClickEvent({ clickEvent })),
), { functional: true });

/** add-to-wishlist */
/**
 * Gestiona los casos en los que se añade un producto a la wishlist desde los items de cart, página de PDP y fast sint.
 */
const clickAddToWishlist$ = createEffect((
    actions$ = inject(Actions),
    zenitEventParserService = inject(ZenitEventParserService),
) => actions$.pipe(
    ofType(AddItemsWishlistAction),
    map(({ items, config }) => {
        const wishlistItems = items.map((item) => item.item);
        const eventFromParsed = config?.eventFrom || getEventFromOrigin[config?.location];

        return zenitEventParserService.getEventObject<ClickEvent>(
            ZenitEventName.Click,
            eventFromParsed,
            {
                action: ZenitClickAction['add-to-wishlist'],
                items: wishlistItems,
                place: config?.place,
                relatedsSectionId: config?.relatedsSectionId,
            },
        );
    }),
    map((clickEvent) => ZenitClickEvent({ clickEvent })),
), { functional: true });

/** remove-from-cart */
/**
 * Gestiona los casos en los que se elimina un producto del carrito desde el cart o se añade un producto del cart a la wishlist.
 */
const clickRemoveFromCart$ = createEffect((
    actions$ = inject(Actions),
    zenitEventParserService = inject(ZenitEventParserService),
) => actions$.pipe(
    ofType(DeleteItemsShopcart),
    map(({ items }) => {
        return zenitEventParserService.getEventObject<ClickEvent>(
            ZenitEventName.Click,
            EventFrom.shopcart,
            {
                action: ZenitClickAction['remove-from-cart'],
                items,
            },
        );
    }),
    map((clickEvent) => ZenitClickEvent({ clickEvent })),
), { functional: true });

/** remove-from-wishlist */
/**
 * Gestiona los casos en los que se elimina un producto de la wishlist desde el icono de PDP y desde wishlist.
 */
const clickRemoveFromWishlist$ = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    zenitEventParserService = inject(ZenitEventParserService),
) => actions$.pipe(
    ofType(WishlistPublicActions.DeleteItemWishlistAction),
    map(({ item }) => {
        const wishlistItems = store.selectSignal(WishlistPublicSelectors.getWishlistItems)();
        const wlItem = wishlistItems.find(content => content.id === item?.productId);

        return zenitEventParserService.getEventObject<ClickEvent>(
            ZenitEventName.Click,
            EventFrom.wishList,
            {
                action: ZenitClickAction['remove-from-wishlist'],
                items: [wlItem],
            },
        );
    }),
    map((clickEvent) => ZenitClickEvent({ clickEvent })),
), { functional: true });

/**
 * Output effect
 */
export const clickEvent$ = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    zenitEventApiService = inject(ZenitEventApiService),
) => actions$.pipe(
    ofType(ZenitClickEvent),
    concatLatestFrom(() => store.select(getCmsConfigValue('enabled_zenit'))),
    filter(([_, enabledZenit]) => enabledZenit),
    concatMap(([{ clickEvent }]) => zenitEventApiService.postEvent$(clickEvent, 'click.input.v1').pipe(
        map(() => ZenitEventSuccess<ClickEvent>()({ eventData: clickEvent })),
        catchError((error) => of(ZenitEventError({ error }))),
    )),
), { functional: true });

export const ClickEventEffects = {
    clickAddQuantityShopcart$,
    clickAddToCart$,
    clickAddToCartFichaBundle$,
    clickAddToWishlist$,
    clickEvent$,
    clickRemoveFromCart$,
    clickRemoveFromWishlist$,
    clickSelectColor$,
    clickSelectItemActions$,
    clickSelectItemElementObserver$,
    clickSelectSize$,
};
